// js/sections/sticky-nav.js

const observer = function observeElementAtTop(el, offset = "0px 0px 0px 0px", callback, threshold = 0) {
  let margin = offset.includes(" ") ? offset : `-${offset}px 0px 0px 0px`;
  const observer = new IntersectionObserver(
    ([entry]) => {
      callback(entry);
    },
    {
      root: null,
      rootMargin: margin,
      threshold: threshold,
    },
  );

  observer.observe(el);
  return observer;
};

function isView(element, callback, options = {}) {
  const { margin = "0px", amount = 0, root = null, once = false } = options;

  let hasTriggered = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const isInView = entry.intersectionRatio >= amount;

        if (once && hasTriggered && !isInView) {
          return;
        }

        if (isInView && once) {
          hasTriggered = true;
        }

        callback(isInView, entry);
      });
    },
    {
      root,
      rootMargin: margin,
      threshold: typeof amount === "number" ? [amount] : amount,
    },
  );

  observer.observe(element);

  return () => observer.unobserve(element);
}

var StickyNav = class extends HTMLElement {
  constructor() {
    super();
    this.menuItems = [];
    this.currentIndex = 0;
    this._scrollHandler = null;
    this._headerContainer = null;
    this._sectionWrapper = null;
    this._lastScrollY = 0;
    this._isHeaderVisible = true;
    this._product2508Observer = null;
    this._scrollEndTimer = null;
    this._isProgrammaticScrolling = false;
    this._headerResizeObserver = null;
    this._loadHandler = null;
    this._visibilityHandler = null;
    this._lastSectionTop = null;
    this._lastHeaderHeight = null;
    this._hamburgerBtn = null;
    this._menuDrawer = null;
    this._externalHashLinkHandler = null;
    this._resizeHandler = null;
    this._scrollRafId = null;
  }

  connectedCallback() {
    this._sectionWrapper = this.closest(".section-sticky-nav") || this.closest(".section-page-footer-button");
    this._init();
    this._setupScrollListener();
    this._setupEventListeners();
    this._setupVisibilityListener();
    this._setupExternalHashLinkListener();
  }

  disconnectedCallback() {
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    if (this._product2508Observer) {
      this._product2508Observer.disconnect();
    }
    if (this._headerResizeObserver) {
      this._headerResizeObserver.disconnect();
    }
    if (this._loadHandler) {
      window.removeEventListener("load", this._loadHandler);
    }
    if (this._visibilityHandler) {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
    }
    if (this._externalHashLinkHandler) {
      document.removeEventListener("click", this._externalHashLinkHandler, true);
    }
    if (this._scrollEndTimer) {
      window.clearTimeout(this._scrollEndTimer);
      this._scrollEndTimer = null;
    }
    if (this._scrollRafId !== null) {
      window.cancelAnimationFrame(this._scrollRafId);
      this._scrollRafId = null;
    }
  }

  _init() {
    this.menuItems = Array.from(
      this.querySelectorAll(".sticky-nav-item, .page-footer-button-item, .page-footer-menu-drawer-item"),
    ).map((item) => ({
      element: item,
      title: item.getAttribute("data-title") || item.textContent.trim(),
      targetId: item.getAttribute("data-target-id"),
      targetElement: null,
    }));

    this.menuItems.forEach((item) => {
      if (item.targetId) {
        item.targetElement = document.getElementById(item.targetId);
      }
    });

    this._hamburgerBtn = this.querySelector(".page-footer-hamburger");
    this._menuDrawer = this.querySelector(".page-footer-menu-drawer");

    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      this._setHeaderHeight(this._headerContainer.getBoundingClientRect().height);
      this._setupProduct2508Observer();
      this._setupHeaderObserver();
    }

    this._updateActiveItem(0);
    this._syncSectionTop();
    this._syncSectionTopAfterTransition();

    this._loadHandler = () => this._syncSectionTopAfterTransition();
    window.addEventListener("load", this._loadHandler, { once: true });
  }

  _setupScrollListener() {
    this._scrollHandler = this._rafThrottle(() => {
      this._handleHeaderVisibility();
    });

    window.addEventListener("scroll", this._scrollHandler, { passive: true });

    this._resizeHandler = this._throttle(() => {
      this._handleHeaderVisibilityResize();
    }, 50);
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  _handleHeaderVisibilityResize() {
    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      const rect = this._headerContainer.getBoundingClientRect();
      this._setHeaderHeight(rect.height);
      this._setupProduct2508Observer();
      this._syncSectionTop(Math.max(0, Math.min(rect.bottom, rect.height)));
    }
  }

  _setupProduct2508Observer() {
    if (this._product2508Observer) {
      this._product2508Observer.disconnect();
    }

    const product2508 = document.querySelector(".product-2508");
    if (product2508) {
      this._product2508Observer = observer(product2508, this.dataset.headerHeight, (entry) => {
        this.isIntersecting = entry.isIntersecting;
      });
    }
  }

  _setupHeaderObserver() {
    if (!this._headerContainer) return;

    if (this._headerResizeObserver) {
      this._headerResizeObserver.disconnect();
    }

    this._headerResizeObserver = new ResizeObserver(() => {
      this._setHeaderHeight(this._headerContainer.getBoundingClientRect().height);
      this._syncSectionTop();
    });

    this._headerResizeObserver.observe(this._headerContainer);
  }

  _setupVisibilityListener() {
    this._visibilityHandler = () => {
      if (document.visibilityState !== "visible") return;
      // 切回标签时按当前一级标题实际可见高度重新同步二级 nav 的 top，避免出现顶部空隙
      window.requestAnimationFrame(() => {
        this._syncSectionTop();
        window.setTimeout(() => this._syncSectionTop(), 100);
      });
    };
    document.addEventListener("visibilitychange", this._visibilityHandler);
  }

  _setupExternalHashLinkListener() {
    this._externalHashLinkHandler = this._handleExternalHashLinkClick.bind(this);
    document.addEventListener("click", this._externalHashLinkHandler, true);
  }

  _handleExternalHashLinkClick(event) {
    const target = event.target && event.target.closest ? event.target.closest('a[href*="#"]') : null;

    if (
      !target ||
      event.defaultPrevented ||
      target.closest(".sticky-nav-item, .page-footer-button-item, .page-footer-menu-drawer-item")
    ) {
      return;
    }

    let url;
    try {
      url = new URL(target.href, window.location.href);
    } catch (error) {
      return;
    }

    if (url.hash === "" || url.pathname !== window.location.pathname || url.search !== window.location.search) {
      return;
    }

    const targetId = decodeURIComponent(url.hash.slice(1));
    if (!targetId || !document.getElementById(targetId)) {
      return;
    }

    this._isProgrammaticScrolling = true;
    this._scheduleProgrammaticScrollUnlock();
  }

  _setupEventListeners() {
    this.menuItems.forEach((item, index) => {
      item.element.addEventListener("click", (e) => {
        this.currentIndex = index;
        this._updateActiveItem(index);

        if (!item.targetElement) {
          return;
        }

        e.preventDefault();
        this._scrollToTarget(index);

        if (this._menuDrawer && this._menuDrawer.getAttribute("aria-hidden") === "false") {
          this._toggleMenu();
        }
      });
    });

    if (this._hamburgerBtn) {
      this._hamburgerBtn.addEventListener("click", () => this._toggleMenu());
    }
  }

  _toggleMenu() {
    if (!this._menuDrawer || !this._hamburgerBtn) return;

    const isExpanded = this._hamburgerBtn.getAttribute("aria-expanded") === "true";
    this._hamburgerBtn.setAttribute("aria-expanded", !isExpanded);
    this._menuDrawer.setAttribute("aria-hidden", isExpanded);

    const icons = this._hamburgerBtn.querySelectorAll("svg");
    if (icons.length === 2) {
      icons[0].classList.toggle("hidden", !isExpanded);
      icons[1].classList.toggle("hidden", isExpanded);
    }
  }

  _handleHeaderVisibility() {
    // ---- 读阶段:本帧仅读取一次 header 尺寸,后续复用 ----
    const currentScrollY = window.scrollY;
    const scrollDirection = currentScrollY > this._lastScrollY ? "down" : "up";
    const hideHeaderOnScrollUp = this.dataset.hideHeaderOnScrollUp === "true";

    const headerRect = this._headerContainer ? this._headerContainer.getBoundingClientRect() : null;
    const visibleHeaderHeight = headerRect ? Math.max(0, Math.min(headerRect.bottom, headerRect.height)) : 0;
    const headerHeight = headerRect ? headerRect.height : 0;

    // ---- 写阶段:控制 header 显隐 + 同步二级 nav 的 top ----
    if (scrollDirection === "down" && !this.isIntersecting) {
      this._hideHeader();
    } else if (scrollDirection === "up") {
      if (hideHeaderOnScrollUp && !this.isIntersecting) {
        this._hideHeader();
      } else {
        this._showHeader();
      }
    }
    this._syncSectionTop(visibleHeaderHeight);

    if (!this._isProgrammaticScrolling) {
      this._updateActiveByScrollPosition(headerHeight);
    } else {
      this._scheduleProgrammaticScrollUnlock();
    }

    this._lastScrollY = currentScrollY;
  }

  _getVisibleHeaderHeight() {
    if (!this._headerContainer) return 0;

    const rect = this._headerContainer.getBoundingClientRect();
    return Math.max(0, Math.min(rect.bottom, rect.height));
  }

  _getHeaderHeight() {
    if (!this._headerContainer) return 0;

    return this._headerContainer.getBoundingClientRect().height;
  }

  _syncSectionTop(visibleHeaderHeight) {
    if (!this._sectionWrapper) return;

    const height = typeof visibleHeaderHeight === "number" ? visibleHeaderHeight : this._getVisibleHeaderHeight();
    const nextTop = `${height}px`;
    if (nextTop === this._lastSectionTop) return;

    this._sectionWrapper.style.top = nextTop;
    this._lastSectionTop = nextTop;
  }

  _setHeaderHeight(height) {
    const nextHeight = String(height);
    if (nextHeight === this._lastHeaderHeight) return;

    this.dataset.headerHeight = nextHeight;
    this._lastHeaderHeight = nextHeight;
  }

  _hideHeader() {
    if (!this._headerContainer || !this._isHeaderVisible) return;

    this._headerContainer.style.transform = "translateY(-100%)";
    this._headerContainer.style.transition = "transform 0.18s ease";
    this._isHeaderVisible = false;
    this._syncSectionTopAfterTransition();
  }

  _showHeader() {
    if (!this._headerContainer || this._isHeaderVisible) return;

    this._headerContainer.style.transform = "translateY(0)";
    this._headerContainer.style.transition = "transform 0.18s ease";
    this._isHeaderVisible = true;
    this._syncSectionTopAfterTransition();
  }

  _syncSectionTopAfterTransition() {
    window.requestAnimationFrame(() => this._syncSectionTop());
    window.setTimeout(() => this._syncSectionTop(), 220);
  }

  _scrollToTarget(index) {
    const item = this.menuItems[index];
    if (!item || !item.targetElement) return;

    const headerHeight = this._getHeaderHeight();
    const stickyNavHeight = this._sectionWrapper
      ? this._sectionWrapper.getBoundingClientRect().height
      : this.getBoundingClientRect().height;
    const safeOffset = Math.max(0, headerHeight + stickyNavHeight);
    const targetTop = item.targetElement.getBoundingClientRect().top + window.pageYOffset - safeOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth",
    });

    this._isProgrammaticScrolling = true;
    this.currentIndex = index;
    this._updateActiveItem(this.currentIndex);
    this._scheduleProgrammaticScrollUnlock();
  }

  _scheduleProgrammaticScrollUnlock() {
    if (this._scrollEndTimer) {
      window.clearTimeout(this._scrollEndTimer);
    }

    // 以“滚动停止”作为解锁条件，避免固定时长导致点击后过程中的 tab 跳动
    this._scrollEndTimer = window.setTimeout(() => {
      this._isProgrammaticScrolling = false;
      this._scrollEndTimer = null;
      this._updateActiveByScrollPosition();
    }, 160);
  }

  _updateActiveByScrollPosition(headerHeight) {
    const stickyNavHeight = this._sectionWrapper
      ? this._sectionWrapper.getBoundingClientRect().height
      : this.getBoundingClientRect().height;
    const scrollY = window.scrollY;
    const hh = typeof headerHeight === "number" ? headerHeight : this._getHeaderHeight();
    const anchorY = scrollY + hh + stickyNavHeight + 1;
    let activeIndex = 0;
    const documentHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    const viewportBottom = scrollY + window.innerHeight;
    const isAtPageBottom = viewportBottom >= documentHeight - 2;
    let lastTargetIndex = -1;
    let lastTargetBottom = 0;

    this.menuItems.forEach((item, index) => {
      if (!item.targetElement) return;

      const itemRect = item.targetElement.getBoundingClientRect();
      const itemTop = itemRect.top + scrollY;
      lastTargetIndex = index;
      lastTargetBottom = itemRect.bottom + scrollY;

      if (itemTop <= anchorY) {
        activeIndex = index;
      }
    });

    if (lastTargetIndex !== -1 && (isAtPageBottom || lastTargetBottom <= viewportBottom)) {
      activeIndex = lastTargetIndex;
    }

    if (activeIndex !== this.currentIndex) {
      this.currentIndex = activeIndex;
      this._updateActiveItem(activeIndex);
    }
  }

  _updateActiveItem(index) {
    const activeItem = this.menuItems[index];
    if (!activeItem) return;

    this.menuItems.forEach((item) => {
      const isActive = item.targetId === activeItem.targetId;
      item.element.classList.toggle("active", isActive);
      item.element.setAttribute("aria-current", String(isActive));
    });
    this._scrollActiveItemIntoView();
  }

  /** 将当前激活的二级标签横向滚动到可见（居中），与页面滚动到的 target 对应 */
  _scrollActiveItemIntoView() {
    const item = this.menuItems[this.currentIndex];
    if (!item || !item.element) return;

    const el = item.element;
    const scrollHost = this; // .sticky-nav 为 overflow-x: auto 的滚动容器

    if (scrollHost.scrollWidth <= scrollHost.clientWidth) return;

    const hostRect = scrollHost.getBoundingClientRect();
    const itemRect = el.getBoundingClientRect();
    const itemCenter = itemRect.left - hostRect.left + itemRect.width / 2;
    const hostCenter = scrollHost.clientWidth / 2;
    const targetScrollLeft = scrollHost.scrollLeft + itemCenter - hostCenter;
    const maxScroll = scrollHost.scrollWidth - scrollHost.clientWidth;

    scrollHost.scrollTo({
      left: Math.max(0, Math.min(targetScrollLeft, maxScroll)),
      behavior: "smooth",
    });
  }

  _throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  // 与浏览器渲染帧对齐的节流:每帧最多执行一次,回调内读取实时 scrollY,
  // 因此天然包含滚动停止时的最后一次状态(trailing 安全)。
  _rafThrottle(func) {
    return () => {
      if (this._scrollRafId !== null) return;
      this._scrollRafId = window.requestAnimationFrame(() => {
        this._scrollRafId = null;
        func();
      });
    };
  }
};

if (!window.customElements.get("sticky-nav")) {
  window.customElements.define("sticky-nav", StickyNav);
}
